import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { format } from 'date-fns';
import { Accordion, AccordionDetails, AccordionSummary, Box, IconButton, Tooltip, Typography } from '@material-ui/core';
import { Delete, Done, Edit, Event, ExpandMore, Timeline, Restore } from '@material-ui/icons';
import { USER_OPERATIONS } from 'helper';
import { RootState } from 'interfaces';
import { IRealEstateEvent, IRealEstateEventType } from '../interfaces';
import {
  CloseRealEstateEventDialog,
  DeleteOrRestoreRealEstateEventDialog,
  EventHistoryDialog,
  ExtendRealEstateEventDialog,
  RealEstateEventDialog,
} from '../components';
import { useDeleteOrRestoreRealEstateEvent } from '../hooks';

interface IProps {
  realEstateId: string;
  realEstateEvent: IRealEstateEvent;
  realEstateEventTypes: IRealEstateEventType[];
  onCreateOrUpdate(realEstateEvent: IRealEstateEvent): void;
}

const RealEstateEvent = ({ realEstateId, realEstateEvent, realEstateEventTypes, onCreateOrUpdate }: IProps) => {
  const { active, descKz, descRu, eventStartTime, realEstateEventTypeName, isDeleted } = realEstateEvent;

  // Global data
  const operations = useSelector((state: RootState) => state.operations);

  const [updateEventDialogOpened, setOpenedUpdateEventDialog] = useState(false);
  const [extendEventDialogOpened, setOpenedExtendEventDialog] = useState(false);
  const [eventHistoryDialogOpened, setOpenedEventHistoryDialog] = useState(false);
  const [closeEventDialogOpened, setOpenedCloseEventDialog] = useState(false);
  const [deleteEventDialogOpened, setOpenedDeleteEventDialog] = useState(false);
  const [eventEndTime, setEventEndTime] = useState(realEstateEvent.eventEndTime);
  const [eventIsActive, setActiveEvent] = useState(active);
  const [eventIsDeleted, setDeletedEvent] = useState(isDeleted);

  const { deleteOrRestoreEventState, handleDeleteOrRestoreEvent } = useDeleteOrRestoreRealEstateEvent(
    realEstateEvent,
    () => setDeletedEvent(false),
  );

  return (
    <Accordion>
      <AccordionSummary expandIcon={<ExpandMore />}>
        <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
          <Box sx={{ display: 'inline-flex', flex: 1 }}>
            <Box sx={{ mr: 1, minWidth: '30%' }}>
              <Typography>{realEstateEventTypeName}</Typography>
            </Box>

            <Typography color="textSecondary">
              {'с ' +
                format(new Date(eventStartTime), 'dd.MM.yyyy HH:mm') +
                ' по ' +
                format(new Date(eventEndTime), 'dd.MM.yyyy HH:mm')}
            </Typography>
          </Box>

          {eventIsActive ? (
            <>
              {operations.includes(USER_OPERATIONS.REAL_ESTATE_EVENT_OPERATION) && (
                <Box sx={{ display: 'flex', alignItems: 'center' }} onClick={(event) => event.stopPropagation()}>
                  <Tooltip title="Редактировать">
                    <IconButton
                      size="small"
                      aria-label="Редактировать"
                      onClick={() => setOpenedUpdateEventDialog(true)}
                    >
                      <Edit />
                    </IconButton>
                  </Tooltip>

                  <Box sx={{ ml: 1 }}>
                    <Tooltip title="Продлить">
                      <IconButton size="small" aria-label="Продлить" onClick={() => setOpenedExtendEventDialog(true)}>
                        <Event />
                      </IconButton>
                    </Tooltip>
                  </Box>

                  <Box sx={{ ml: 1 }}>
                    <Tooltip title="История изменений">
                      <IconButton
                        size="small"
                        aria-label="История изменений"
                        onClick={() => setOpenedEventHistoryDialog(true)}
                      >
                        <Timeline />
                      </IconButton>
                    </Tooltip>
                  </Box>

                  <Box sx={{ ml: 1 }}>
                    <Tooltip title="Завершить">
                      <IconButton size="small" aria-label="Завершить" onClick={() => setOpenedCloseEventDialog(true)}>
                        <Done />
                      </IconButton>
                    </Tooltip>
                  </Box>

                  <Box sx={{ ml: 1 }}>
                    {eventIsDeleted ? (
                      <Tooltip title="Восстановить">
                        <IconButton
                          size="small"
                          aria-label="Восстановить"
                          disabled={deleteOrRestoreEventState.loading}
                          onClick={handleDeleteOrRestoreEvent}
                        >
                          <Restore />
                        </IconButton>
                      </Tooltip>
                    ) : (
                      <Tooltip title="Удалить">
                        <IconButton size="small" aria-label="Удалить" onClick={() => setOpenedDeleteEventDialog(true)}>
                          <Delete />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                </Box>
              )}
            </>
          ) : (
            <Typography variant="caption" color="textSecondary">
              Событие завершено
            </Typography>
          )}
        </Box>
      </AccordionSummary>

      <AccordionDetails>
        <Box>
          <Typography>Описание:</Typography>

          <Box sx={{ display: 'flex' }}>
            {descKz && (
              <Box sx={{ mr: 1 }}>
                <Typography variant="caption" color="textSecondary">
                  {descKz}
                </Typography>
              </Box>
            )}

            {descRu && (
              <Box sx={{ ml: 1 }}>
                <Typography variant="caption" color="textSecondary">
                  {descRu}
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      </AccordionDetails>

      <RealEstateEventDialog
        realEstateId={realEstateId}
        realEstateEvent={realEstateEvent}
        realEstateEventTypes={realEstateEventTypes}
        open={updateEventDialogOpened}
        onCreateOrUpdate={onCreateOrUpdate}
        onClose={() => setOpenedUpdateEventDialog(false)}
      />

      <ExtendRealEstateEventDialog
        realEstateEvent={realEstateEvent}
        open={extendEventDialogOpened}
        onExtend={setEventEndTime}
        onClose={() => setOpenedExtendEventDialog(false)}
      />

      <EventHistoryDialog
        realEstateEvent={realEstateEvent}
        open={eventHistoryDialogOpened}
        onClose={() => setOpenedEventHistoryDialog(false)}
      />

      <CloseRealEstateEventDialog
        realEstateEvent={realEstateEvent}
        open={closeEventDialogOpened}
        onCloseEvent={() => setActiveEvent(false)}
        onClose={() => setOpenedCloseEventDialog(false)}
      />

      <DeleteOrRestoreRealEstateEventDialog
        realEstateEvent={realEstateEvent}
        open={deleteEventDialogOpened}
        onDelete={() => setDeletedEvent(true)}
        onClose={() => setOpenedDeleteEventDialog(false)}
      />
    </Accordion>
  );
};

export default RealEstateEvent;
